import { isString } from "lodash";
import { enqueueSnackbar } from "notistack";
import { useState, useEffect, useCallback } from "react";

import { Stack } from "@mui/system";
import { LoadingButton } from "@mui/lab";
import { Card, Avatar, Tooltip, TextField, IconButton, Typography, CardContent, Autocomplete, ListItemText } from "@mui/material";

import { useBoolean } from "src/hooks/use-boolean";
import { useOrgTenant } from "src/hooks/auth/use-org-tenant";
import { useAuthContext } from "src/hooks/auth/use-auth-context";

import { useTranslate } from "src/locales";
import { useAppSelector } from "src/store/store";
import { User } from "src/services/organisation/organisation.types";
import { getUpdateAssigneeErrorMessage } from "src/services/deal-flow/deal-flow.utils";
import { useGetDealQuery, useUpdateDealAssigneeMutation } from "src/services/deal-flow/deal-flow.service";

import Iconify from "src/components/iconify";
import { ConfirmDialog } from "src/components/custom-dialog";
import AccountDisplay from "src/components/account-viewer/account-display";

type Props = {
  dealId: string;
}

const DealOwnerCard = ({ dealId }: Props) => {

  const showEditUserDialog = useBoolean();

  const { t } = useTranslate();

  const tenantType = useOrgTenant();

  const authContext = useAuthContext();

  const { data: deal } = useGetDealQuery({ dealId }, {
    skip: !dealId,
  });

  const users = useAppSelector((state) => state?.data?.users);

  const [updateDealAssignee, { isLoading: isUpdatingAssignee }] = useUpdateDealAssigneeMutation();

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    if (deal?.assigned_to && users?.length) {
      const userAccount = users.find((user) => user.id === deal.assigned_to?.id);
      setSelectedUser(userAccount ?? null);
      setCurrentUser(userAccount ?? null);
    }
  }, [deal?.assigned_to, users]);

  const handleUpdateAccountOwner = useCallback(async () => {
    if (!deal || !selectedUser) return;

    try {
      await updateDealAssignee({
        dealId,
        data: {
          assigned_to_id: selectedUser.id as string
        },
      }).unwrap();

      enqueueSnackbar(t('deal_flow.api.update_assigned_account.success'), { variant: 'success' });
      showEditUserDialog.onFalse();
    } catch (error) {
      enqueueSnackbar(getUpdateAssigneeErrorMessage(error), { variant: 'error' });
    }
  }, [deal, selectedUser, showEditUserDialog, dealId, updateDealAssignee, t]);

  return (
    <>
      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Stack direction="row" spacing={2} display="flex" alignItems="center">
              <Avatar
                alt={currentUser ? currentUser.first_name : undefined}
                src={currentUser ? currentUser?.profile_image?.public_path : undefined}
                variant="rounded"
                sx={{ width: 48, height: 48 }}
              />

              <Stack direction="row" display="flex" width="100%" justifyContent="space-between">
                <ListItemText
                  primary={t('deal_flow.detail.owner.title')}
                  secondary={
                    currentUser ?
                      <AccountDisplay
                        accountId={currentUser.id as string}
                        textFieldProps={{
                          limit: 15,
                          text: `${currentUser.first_name} ${currentUser.last_name}`,
                          mode: 'characters',
                          showMoreEnabled: false,
                          variant: 'body2'
                        }}
                      />
                      :
                      <Typography variant="body2" color="text.secondary">
                        {t('deal_flow.detail.owner.no_account_owner')}
                      </Typography>
                  }
                />

                <Stack direction="row" spacing={1} display="flex" alignItems="center">

                  <Tooltip title={t('deal_flow.detail.owner.update_tooltip')} arrow placement="top">
                    <IconButton onClick={showEditUserDialog.onTrue} sx={{ p: .3 }}>
                      <Iconify icon="solar:pen-bold" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {
        (deal && users?.length !== undefined) && (
          <ConfirmDialog
            open={showEditUserDialog.value}
            title={t('deal_flow.detail.owner.edit')}
            onClose={showEditUserDialog.onFalse}
            content={
              <Autocomplete
                sx={{
                  paddingTop: 2,
                }}
                multiple={false}
                options={users as User[]}
                value={selectedUser as User}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t(
                      'admin-organization.department.department_add_user.user_autocomplete.label'
                    )}
                    placeholder={t(
                      'admin-organization.department.department_add_user.user_autocomplete.placeholder'
                    )}
                  />
                )}
                onChange={(_e, value: User | null) => setSelectedUser(value)}
                renderOption={({ key, ...rest }, option: User) => (
                  <li key={key} {...rest}>
                    <Stack direction="column">
                      <Typography variant="body1">
                        {`${option.first_name} ${option.last_name}`}
                      </Typography>
                    </Stack>
                  </li>
                )}
                getOptionLabel={(option) =>
                  !isString(option) ? `${option.first_name} ${option.last_name}` : 'Assign to me'
                }
                isOptionEqualToValue={(option, value) =>
                  option && (option as User).id === (value as User).id
                }
                getOptionKey={(option) => (option as User).id as string}
              />
            }
            action={
              <LoadingButton
                variant="contained"
                disabled={!selectedUser}
                onClick={handleUpdateAccountOwner}
                loading={isUpdatingAssignee}>
                {t('deal_flow.detail.owner.assign')}
              </LoadingButton>
            }
          />
        )
      }

    </>
  )

}

export default DealOwnerCard;